import React from "react";
import { Link } from "react-router-dom";
import SectionHeading from "./SectionHeading";
import { LANDING } from "@/constants/testIds";

/**
 * Selective Capital Pathways — Homepage Section (04)
 * Lifts to dedicated detail page at /capital-pathways.
 */
const FAMILY_OFFICE_STEPS = [
  { n: "01", title: "Define the Mandate" },
  { n: "02", title: "Screen & Structure" },
  { n: "03", title: "Support Execution" },
];

const SelectiveCapitalPathways = ({ onRequestAccess }) => {
  const PILLARS = [
    {
      n: "01",
      title: "Direct Platform Capital",
      body:
        "Opportunities that meet platform criteria may be considered for direct platform capital formation through the partnership's vehicle architecture.",
    },
    {
      n: "02",
      title: "Investor Access",
      body:
        "Selectively documented co-investment and single-asset SPV opportunities, advanced on a confidential, by-introduction basis.",
    },
    {
      n: "03",
      title: "Fund-Structured Solutions",
      body:
        "Capital deployed through the platform's intended fund architecture, calibrated to the duration, governance and reporting preferences of institutional capital.",
    },
    {
      n: "04",
      title: "Coordinated Introductions",
      body:
        "Where appropriate, the partnership may coordinate introductions to selected equity, private credit, real estate debt or strategic capital partners within its external network.",
    },
  ];

  return (
    <section
      id="capital-pathways"
      data-testid={LANDING.capitalPathways}
      className="relative py-32 lg:py-44 border-t border-[var(--kr-line-soft)]"
    >
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-16 items-end mb-20">
          <div className="lg:col-span-8">
            <SectionHeading
              index="04"
              label="Capital Pathways · Mandate-Led"
              title={
                <>
                  Selective{" "}
                  <span className="italic text-[var(--kr-gold)]">
                    Capital Pathways
                  </span>
                  .
                </>
              }
            />
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/capital-pathways"
              data-testid={LANDING.capitalPathwaysReadMore}
              className="kr-link font-mono-label text-[11px]"
            >
              Read the Pathways Framework →
            </Link>
          </div>
        </div>

        {/* Lead statement — left-aligned plain body, matches FundArchitecture pattern */}
        <p
          data-testid={LANDING.capitalPathwaysLead}
          className="text-[var(--kr-text-muted)] text-[16px] leading-[1.85] max-w-[80ch] mb-16"
        >
          Kellermann Reid &amp; Co. is mandate-led. Opportunities that meet
          our platform criteria may be considered for direct platform capital
          formation, investor access, or fund-structured solutions. Where a
          transaction sits outside our direct mandate parameters, we may,
          where appropriate, coordinate introductions to selected equity,
          private credit, real estate debt, or strategic capital partners
          within our external network.
        </p>

        {/* Four pathways — now clickable to detail page */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-[var(--kr-line)]">
          {PILLARS.map((p) => (
            <Link
              key={p.n}
              to="/capital-pathways"
              data-testid={`${LANDING.capitalPathwaysPillarPrefix}${p.n}`}
              className="p-10 lg:p-11 border-r border-b border-[var(--kr-line)] block group hover:bg-[rgba(201,169,110,0.025)] transition-colors duration-500"
            >
              <div className="flex items-baseline justify-between mb-8">
                <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                  {p.n} / 04
                </span>
                <span
                  aria-hidden
                  className="font-serif-display italic text-[var(--kr-text-dim)] text-2xl group-hover:text-[var(--kr-gold)] transition-colors"
                >
                  →
                </span>
              </div>
              <h3 className="font-serif-display text-[1.45rem] leading-[1.2] text-[var(--kr-text)] mb-5 group-hover:text-[var(--kr-gold)] transition-colors duration-500 min-h-[2.4em]">
                {p.title}
              </h3>
              <p className="text-[var(--kr-text-muted)] text-[14px] leading-[1.75]">
                {p.body}
              </p>
            </Link>
          ))}
        </div>

        {/* Family Office panel — one global service, all jurisdictions */}
        <div
          id="family-offices"
          data-testid={LANDING.capitalPathwaysFamilyOffice}
          className="mt-16 border border-[var(--kr-line)] bg-[var(--kr-bg-2)] scroll-mt-32"
        >
          <div className="grid lg:grid-cols-12">
            <div className="lg:col-span-7 p-10 lg:p-14 border-b lg:border-b-0 lg:border-r border-[var(--kr-line)]">
              <div className="flex items-center gap-3 mb-6">
                <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                  For Family Offices · Global
                </span>
                <span className="w-10 h-px bg-[var(--kr-gold)]/60" />
              </div>
              <h3
                data-testid={LANDING.capitalPathwaysFamilyOfficeTitle}
                className="font-serif-display text-[var(--kr-text)] text-[clamp(1.7rem,2.8vw,2.3rem)] leading-[1.15] mb-8"
              >
                Family Office{" "}
                <span className="italic text-[var(--kr-gold)]">
                  Investment Solutions
                </span>
                .
              </h3>
              <div className="space-y-5 max-w-[62ch]">
                <p className="text-[var(--kr-text)] text-[16px] leading-[1.85]">
                  Kellermann Reid &amp; Co. supports select family offices and
                  strategic capital partners seeking disciplined access to
                  private-market opportunities.
                </p>
                <p className="text-[var(--kr-text-muted)] text-[15px] leading-[1.85]">
                  We work with each family to understand its investment
                  objectives, screen and evaluate aligned opportunities,
                  coordinate commercial and financial diligence, and develop
                  appropriate transaction structures for investment committee
                  consideration.
                </p>
                <p className="text-[var(--kr-text-muted)] text-[15px] leading-[1.85]">
                  Each engagement is tailored to the family&apos;s mandate,
                  governance requirements and jurisdiction, with appropriate
                  legal, tax, regulatory and specialist advisers coordinated
                  where required. The family retains full discretion over all
                  investment decisions.
                </p>
              </div>
            </div>

            <div className="lg:col-span-5 p-10 lg:p-14 flex flex-col">
              <div className="font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-6">
                Process
              </div>
              <ol className="border-t border-[var(--kr-line)] mb-10">
                {FAMILY_OFFICE_STEPS.map((s) => (
                  <li
                    key={s.n}
                    data-testid={`${LANDING.capitalPathwaysFamilyOfficeStepPrefix}${s.n}`}
                    className="flex items-baseline gap-5 py-5 border-b border-[var(--kr-line)]"
                  >
                    <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                      {s.n}
                    </span>
                    <span className="font-serif-display text-[var(--kr-text)] text-[1.25rem] leading-[1.25]">
                      {s.title}
                    </span>
                  </li>
                ))}
              </ol>
              <div className="mt-auto flex flex-col sm:flex-row gap-3">
                <Link
                  to="/family-office-investment-solutions"
                  data-testid={LANDING.capitalPathwaysFamilyOfficeExplore}
                  className="kr-btn-primary justify-center"
                >
                  Explore Family Office Solutions
                  <span aria-hidden>→</span>
                </Link>
                <button
                  type="button"
                  onClick={() => onRequestAccess?.({ interest: "family_office" })}
                  data-testid={LANDING.capitalPathwaysFamilyOfficeCta}
                  className="kr-btn-ghost justify-center"
                >
                  Discuss a Family Office Mandate →
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Compliance footnote */}
        <div className="mt-16 pt-10 border-t border-[var(--kr-line-soft)] grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-3">
            <div className="font-mono-label text-[10px] text-[var(--kr-text-muted)]">
              Subject To
            </div>
          </div>
          <div className="lg:col-span-9">
            <p
              data-testid={LANDING.capitalPathwaysSubject}
              className="text-[var(--kr-text-muted)] text-[14px] leading-[1.85] max-w-[68ch]"
            >
              Any such engagement remains subject to diligence, suitability,
              jurisdictional requirements, and final approval by the relevant
              capital provider. Kellermann Reid &amp; Co. does not act as a
              placement agent or broker-dealer, and does not guarantee funding
              or transaction outcomes.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SelectiveCapitalPathways;
